/**
 * relatedWork — pure helpers for the shared related-work card strip
 * (UOW-4G #3672, contract docs/features/unit-of-work.md §3.11).
 *
 * Data sources, one batched call per subject (never one per card):
 *   - GET /api/v1/related-work/{subjectType}/{subjectId} for task/phase,
 *     PO, bid request, subcontract and tracker-item subjects;
 *   - the estimate editor's existing whole-estimate batch payload
 *     (GET /estimates/{id}/line-item-links, lib/estimateLinkedWork.ts) for
 *     the estimate line peek — zero further requests there.
 *
 * Kept free of React so the mapping/grouping/status logic is unit-testable.
 */
import { authedFetch } from '@/lib/api'
import { poTone, poLabel, bidTone, bidLabel, subTone, subLabel, type StatusTone } from '@/lib/procurement-status'
import type { EstimateLineLinks } from '@/lib/estimateLinkedWork'

export type RelatedWorkKind =
  | 'task' | 'phase' | 'estimate_line'
  | 'purchase_order' | 'bid_request' | 'subcontract' | 'bill'

/** One card, as the endpoint returns it (snake_case preserved). */
export interface RelatedWorkCard {
  kind: RelatedWorkKind
  id: string
  number?: string
  headline: string
  status?: string
  /** Subcontract cards: the /subcontracts-v2 route id (public_id). */
  public_id?: string
  /** Money figure — absent entirely for tokens without estimates.view. */
  figure?: number | null
  is_primary?: boolean
  /** A "link anyway" / manual-picker reference (§3.10 union view): the task
   *  points at a line ANOTHER task owns. Displayed like any line card, but
   *  it neither combines the task nor offers the per-line remove (split). */
  is_secondary?: boolean
  /** Needs-pricing marker (§3.9, #3667): the LINK-ROW boolean, populated by
   *  every backend estimate-line card producer (task/tracker/doc subjects) —
   *  never via line_status, which is tenant vocabulary. The line_status
   *  'needs_pricing' mapping below stays as a fallback only. */
  needs_pricing?: boolean
  quantity?: number | null
  unit?: string
  unit_cost?: number | null
  cost_type?: string
  estimate_name?: string
  estimate_public_id?: string
  /** Target no longer resolves (hard-deleted) or is archived — render the
   *  card inert, never a click into a 404 (contract §5). */
  unavailable?: boolean
  /** UOW-5B (§3.13): this task's sourcing is late against its own schedule.
   *  Server-derived from the one at-risk definition, in one batched lookup
   *  per subject; present only on task/phase cards, and only where the card
   *  itself renders — so it inherits that card's gate and discloses nothing
   *  new (§3.1/§3.11). Carries no money. */
  at_risk?: boolean
  at_risk_reason?: string
}

export interface RelatedWorkResponse {
  subject_type: string
  subject_id: string
  cards: RelatedWorkCard[]
}

export type RelatedWorkSubjectType =
  | 'task' | 'purchase_order' | 'bid_request' | 'subcontract' | 'tracker_item'

/**
 * One batched fetch per subject. Throws on non-OK so callers can degrade the
 * strip to hidden (facet rule: never block the peek); a 403 (no permission
 * for the subject's surface) degrades the same way.
 */
export async function fetchRelatedWork(
  subjectType: RelatedWorkSubjectType,
  subjectId: string | number,
): Promise<RelatedWorkCard[]> {
  const res = await authedFetch(
    `/api/v1/related-work/${subjectType}/${encodeURIComponent(String(subjectId))}`,
    {},
    { redirectOnAuthFailure: false },
  )
  if (!res.ok) throw new Error(`related-work fetch failed: ${res.status}`)
  const data: RelatedWorkResponse = await res.json()
  return Array.isArray(data.cards) ? data.cards : []
}

/**
 * The estimate line peek's cards come from the editor's already-fetched
 * batch payload (#3587) — this maps one line's entry onto the card shape.
 * The payload exists only behind estimates.view, so figures are safe here.
 */
export function cardsFromEstimateLineLinks(links: EstimateLineLinks | undefined | null): RelatedWorkCard[] {
  if (!links) return []
  const cards: RelatedWorkCard[] = []
  for (const t of links.tasks ?? []) {
    cards.push({ kind: 'task', id: t.id, headline: t.name || 'Untitled task', status: t.status })
  }
  for (const d of links.docs ?? []) {
    cards.push({
      kind: d.parent_type,
      id: String(d.parent_id),
      number: d.parent_number,
      headline: d.parent_title || d.parent_number || `#${d.parent_id}`,
      status: d.parent_status,
      public_id: d.parent_public_id,
      figure: (d as { parent_amount?: number | null }).parent_amount ?? null,
    })
  }
  return cards
}

// ─── Kind vocabulary ────────────────────────────────────────────────────────

export const RELATED_KIND_LABEL: Record<RelatedWorkKind, string> = {
  task: 'Task',
  phase: 'Phase',
  estimate_line: 'Estimate line',
  purchase_order: 'PO',
  bid_request: 'Bid',
  subcontract: 'Sub',
  bill: 'Bill',
}

export const RELATED_KIND_GROUP_LABEL: Record<RelatedWorkKind, string> = {
  task: 'Schedule',
  phase: 'Schedule',
  estimate_line: 'Estimate lines',
  purchase_order: 'Purchase orders',
  bid_request: 'Bid requests',
  subcontract: 'Subcontracts',
  bill: 'Bills',
}

/** Chip tone per kind — the #3587 chip vocabulary, extended for the two
 *  kinds the chips never rendered (estimate lines, bills). */
export const RELATED_KIND_TONE: Record<RelatedWorkKind, string> = {
  task: 'lead',
  phase: 'lead',
  estimate_line: 'active',
  purchase_order: 'info',
  bid_request: 'warning',
  subcontract: 'blocked',
  bill: 'draft',
}

const RELATED_KIND_ORDER: RelatedWorkKind[] = [
  'task', 'phase', 'estimate_line', 'purchase_order', 'bid_request', 'subcontract', 'bill',
]

// ─── Status vocabulary per kind (contract: "in that surface's own vocabulary") ─

const TASK_STATUS_TONE: Record<string, StatusTone> = {
  'Not Started': 'not_started',
  'In Progress': 'in_progress',
  'Blocked': 'blocked',
  'Completed': 'completed',
  'Cancelled': 'cancelled',
  'On Hold': 'on_hold',
}

const LINE_STATUS: Record<string, { tone: StatusTone; label: string }> = {
  incomplete: { tone: 'not_started', label: 'Incomplete' },
  estimated: { tone: 'in_progress', label: 'Estimated' },
  verified: { tone: 'completed', label: 'Verified' },
  missing: { tone: 'blocked', label: 'Missing' },
  needs_pricing: { tone: 'blocked', label: 'Needs pricing' },
}

const BILL_STATUS: Record<string, { tone: StatusTone; label: string }> = {
  pending: { tone: 'not_started', label: 'Pending' },
  approved: { tone: 'in_progress', label: 'Approved' },
  partial: { tone: 'in_progress', label: 'Partially paid' },
  paid: { tone: 'completed', label: 'Paid' },
  overdue: { tone: 'blocked', label: 'Overdue' },
  void: { tone: 'cancelled', label: 'Void' },
}


/** Status pill tone + label for a card, in the card's own surface vocabulary. */
export function relatedCardStatus(card: Pick<RelatedWorkCard, 'kind' | 'status' | 'unavailable' | 'needs_pricing'>): { tone: StatusTone; label: string } | null {
  if (card.kind === 'task' || card.kind === 'phase') {
    if (card.unavailable) return { tone: 'cancelled', label: 'Archived' }
    if (!card.status) return null
    return { tone: TASK_STATUS_TONE[card.status] ?? 'not_started', label: card.status }
  }
  // Needs-pricing outranks the line's own status (§3.9). #3667 handshake:
  // the marker moves to the link row and reaches cards as this boolean.
  if (card.kind === 'estimate_line' && card.needs_pricing) {
    return { tone: 'blocked', label: 'Needs pricing' }
  }
  if (!card.status) return null
  switch (card.kind) {
    case 'estimate_line': {
      const m = LINE_STATUS[card.status]
      return m ?? { tone: 'not_started', label: card.status }
    }
    case 'purchase_order':
      return { tone: poTone(card.status), label: poLabel(card.status) }
    case 'bid_request':
      return { tone: bidTone(card.status), label: bidLabel(card.status) }
    case 'subcontract':
      return { tone: subTone(card.status), label: subLabel(card.status) }
    case 'bill': {
      const m = BILL_STATUS[card.status]
      return m ?? { tone: 'not_started', label: card.status }
    }
    default:
      return null
  }
}

// ─── Grouping ("grouped by kind when more than a few") ─────────────────────

export interface RelatedWorkGroup {
  kind: RelatedWorkKind
  label: string
  cards: RelatedWorkCard[]
}

/** Threshold above which the strip renders per-kind group headers. */
export const RELATED_GROUPING_THRESHOLD = 4

export function groupRelatedCards(cards: RelatedWorkCard[]): RelatedWorkGroup[] {
  const byKind = new Map<RelatedWorkKind, RelatedWorkCard[]>()
  for (const c of cards) {
    const list = byKind.get(c.kind)
    if (list) list.push(c)
    else byKind.set(c.kind, [c])
  }
  const groups: RelatedWorkGroup[] = []
  for (const kind of RELATED_KIND_ORDER) {
    const list = byKind.get(kind)
    if (!list || list.length === 0) continue
    // task + phase share one Schedule group.
    if (kind === 'phase' && groups.length > 0 && groups[groups.length - 1].kind === 'task') {
      groups[groups.length - 1].cards.push(...list)
      continue
    }
    groups.push({ kind, label: RELATED_KIND_GROUP_LABEL[kind], cards: list })
  }
  return groups
}

/** True when the strip should render group headers (more than a few cards
 *  spread over more than one kind). */
export function shouldGroupRelatedCards(cards: RelatedWorkCard[]): boolean {
  if (cards.length <= RELATED_GROUPING_THRESHOLD) return false
  return groupRelatedCards(cards).length > 1
}

// ─── Navigation ─────────────────────────────────────────────────────────────

/**
 * The record's own page (the strip's secondary "leave the peek" action).
 * Null when the target is unavailable or has no page to land on.
 */
export function relatedCardPageHref(card: RelatedWorkCard): string | null {
  if (card.unavailable) return null
  switch (card.kind) {
    case 'task':
    case 'phase':
      return `/tasks?task=${card.id}`
    case 'estimate_line':
      return card.estimate_public_id ? `/estimates/${card.estimate_public_id}` : null
    case 'purchase_order':
      return `/procurement?tab=purchase-orders&highlight=${card.id}`
    case 'bid_request':
      return `/procurement?tab=bid-requests&highlight=${card.id}`
    case 'subcontract':
      return `/procurement?tab=subcontracts&highlight=${card.id}`
    case 'bill':
      return `/procurement?tab=bills&highlight=${card.id}`
    default:
      return null
  }
}

/** The meta line under an estimate-line card: "10 ea × $25.00 · Estimate". */
export function relatedCardMeta(card: RelatedWorkCard, formatMoney: (n: number) => string): string {
  if (card.kind !== 'estimate_line' || card.unavailable) return ''
  const parts: string[] = []
  if (card.quantity != null && card.unit_cost != null) {
    parts.push(`${card.quantity} ${card.unit || ''}`.trim() + ` × ${formatMoney(card.unit_cost)}`)
  }
  if (card.estimate_name) parts.push(card.estimate_name)
  return parts.join(' · ')
}

/** Sum of estimate-line figures — the combined task's "Estimate baseline"
 *  row the strip carries forward from the 4F list it supersedes. OWNED
 *  lines only: a secondary reference is another task's baseline. */
export function estimateLineFigureTotal(cards: RelatedWorkCard[]): number | null {
  let total = 0
  let any = false
  for (const c of cards) {
    if (c.kind !== 'estimate_line' || c.unavailable || c.is_secondary) continue
    if (typeof c.figure === 'number') {
      total += c.figure
      any = true
    }
  }
  return any ? total : null
}
